import type { CompetitionElementRow, CompetitionElementsData, ElementStatus } from './api';

export type Scene = 'hook-scene' | 'end-card';

const SCENE_PREFIXES: ReadonlyArray<{ scene: Scene; prefix: string }> = [
  { scene: 'hook-scene', prefix: 'Hook Scene ' },
  { scene: 'end-card', prefix: 'End Card ' },
];

export const ROW_LEVEL_DEPTH: Record<CompetitionElementRow['rowLevel'], number> = {
  category: 0,
  featureCategory: 1,
  feature: 2,
  tag: 3,
};

interface FeatureCategoryGroup {
  featureCategoryRow: CompetitionElementRow;
  leafRows: CompetitionElementRow[];
}

interface CategoryGroup {
  categoryRow: CompetitionElementRow;
  featureCategoryGroups: FeatureCategoryGroup[];
}

export function rowPath(row: CompetitionElementRow): string {
  const segments = [row.category, row.featureCategory, row.feature, row.tag];
  return segments.slice(0, ROW_LEVEL_DEPTH[row.rowLevel] + 1).join('/');
}

export function parentPath(row: CompetitionElementRow): string | null {
  const depth = ROW_LEVEL_DEPTH[row.rowLevel];
  if (depth === 0) return null;
  const segments = [row.category, row.featureCategory, row.feature, row.tag];
  return segments.slice(0, depth).join('/');
}

export function visibleCompetitionElementRows(
  rows: CompetitionElementRow[],
  expandedPaths: ReadonlySet<string>,
): CompetitionElementRow[] {
  const visibleRows: CompetitionElementRow[] = [];
  const ancestorOpen: boolean[] = [];
  for (const row of rows) {
    const depth = ROW_LEVEL_DEPTH[row.rowLevel];
    ancestorOpen.length = depth;
    const visible = depth === 0 || ancestorOpen[depth - 1] === true;
    if (visible) visibleRows.push(row);
    ancestorOpen[depth] = visible && expandedPaths.has(rowPath(row));
  }
  return visibleRows;
}

export function filterRowsByCategory(
  rows: CompetitionElementRow[],
  selectedCategories: string[],
): CompetitionElementRow[] {
  if (selectedCategories.length === 0) return rows;
  return rows.filter((row) => selectedCategories.includes(row.category));
}

export function filterRowsByStatus(
  rows: CompetitionElementRow[],
  selectedStatuses: ElementStatus[],
): CompetitionElementRow[] {
  if (selectedStatuses.length === 0) return rows;
  return rows.filter((row) => row.rowLevel !== 'tag' || selectedStatuses.includes(row.status));
}

export function sceneOf(feature: string): Scene | null {
  return SCENE_PREFIXES.find((entry) => feature.startsWith(entry.prefix))?.scene ?? null;
}

export function filterRowsByScene(
  rows: CompetitionElementRow[],
  activeScenes: readonly Scene[],
): CompetitionElementRow[] {
  if (activeScenes.length === 0) return rows;
  return rows.filter((row) => {
    if (row.rowLevel !== 'tag' && row.rowLevel !== 'feature') return true;
    const scene = sceneOf(row.feature);
    return scene !== null && activeScenes.includes(scene);
  });
}

function shareOf(part: number, whole: number): number {
  return whole === 0 ? 0 : part / whole;
}

/** The topBrand of whichever row carries the strongest competitorShare — a synthetic row has no
 * grain of its own, so it borrows the ranking of the row nesting strongest under it. */
function strongestTopBrand(
  rows: readonly CompetitionElementRow[],
): CompetitionElementRow['topBrand'] {
  let best: CompetitionElementRow['topBrand'] = null;
  let bestShare = -Infinity;
  for (const row of rows) {
    if (row.competitorShare > bestShare) {
      bestShare = row.competitorShare;
      best = row.topBrand;
    }
  }
  return best;
}

function synthesizeRow(
  rowLevel: 'category' | 'featureCategory',
  category: string,
  featureCategory: string,
  children: CompetitionElementRow[],
  total: CompetitionElementsData['total'],
): CompetitionElementRow {
  const competitorCreatives = children.reduce((sum, row) => sum + row.competitorCreatives, 0);
  const clientCreatives = children.reduce((sum, row) => sum + row.clientCreatives, 0);
  return {
    rowLevel,
    category,
    featureCategory: rowLevel === 'category' ? '' : featureCategory,
    feature: '',
    tag: '',
    competitorCreatives,
    competitorShare: shareOf(competitorCreatives, total.competitorCreatives),
    competitors: [...new Set(children.flatMap((row) => row.competitors))].sort(),
    topBrand: strongestTopBrand(children),
    clientCreatives,
    clientShare: shareOf(clientCreatives, total.clientCreatives),
    status: 'well_covered',
  };
}

/** The backend only ever returns `'feature'`/`'tag'` rows (see `COMPETITOR_GROUPING_SETS` in
 * `apps/trpc/actions/src/competition/queries.ts`) — no grain above feature. This synthesizes the
 * missing `'category'`/`'featureCategory'` rows client-side so the 4-level tree the table expects
 * actually exists. Incoming feature/tag rows keep their relative order; only the synthetic levels
 * are sorted, by descending `competitorShare`. */
export function groupCompetitionElementRows(
  rows: CompetitionElementRow[],
  total: CompetitionElementsData['total'],
): CompetitionElementRow[] {
  const leavesByCategory = new Map<string, Map<string, CompetitionElementRow[]>>();
  for (const row of rows) {
    const byFeatureCategory =
      leavesByCategory.get(row.category) ?? new Map<string, CompetitionElementRow[]>();
    leavesByCategory.set(row.category, byFeatureCategory);
    const leaves = byFeatureCategory.get(row.featureCategory) ?? [];
    leaves.push(row);
    byFeatureCategory.set(row.featureCategory, leaves);
  }

  const categoryGroups: CategoryGroup[] = [...leavesByCategory]
    .map(([category, byFeatureCategory]) => {
      const featureCategoryGroups: FeatureCategoryGroup[] = [...byFeatureCategory]
        .map(([featureCategory, leafRows]) => ({
          featureCategoryRow: synthesizeRow(
            'featureCategory',
            category,
            featureCategory,
            leafRows.filter((row) => row.rowLevel === 'feature'),
            total,
          ),
          leafRows,
        }))
        .sort(
          (left, right) =>
            right.featureCategoryRow.competitorShare - left.featureCategoryRow.competitorShare,
        );

      const categoryRow = synthesizeRow(
        'category',
        category,
        '',
        featureCategoryGroups.map((group) => group.featureCategoryRow),
        total,
      );

      return { categoryRow, featureCategoryGroups };
    })
    .sort((left, right) => right.categoryRow.competitorShare - left.categoryRow.competitorShare);

  return categoryGroups.flatMap(({ categoryRow, featureCategoryGroups }) => [
    categoryRow,
    ...featureCategoryGroups.flatMap(({ featureCategoryRow, leafRows }) => [
      featureCategoryRow,
      ...leafRows,
    ]),
  ]);
}
